import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getStates } from '../../../helpers/getStates';
import '../../../styles/Testimonial/testimonialHeader.scss';
import { FormField } from '../../UI/General/FormField';

/**
 * TestiHeader component for collecting event information
 * @param {Object} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const TestiHeader = ({ orderID, externalOrderID, data }) => {
  const dataValues = data.data || data;
  console.log('TestiHeader data:', dataValues);

  // Normalize country name for display
  const displayCountry = dataValues.shipping_country === 'US' ? 'USA' : dataValues.shipping_country;

  const initialValues = {
    eventCity: dataValues.shipping_city,
    eventCountry: displayCountry,
    eventState: dataValues.shipping_state,
  };

  const [states, setStates] = useState([]);
  const [weddingDate, setWeddingDate] = useState(dataValues.event_date);
  const [dateRange, setDateRange] = useState({ min: '', max: '' });
  const [formValues, setFormValues] = useState({
    city: initialValues.eventCity,
    country: initialValues.eventCountry,
    state: initialValues.eventState,
  });

  // Initialize states based on normalized country
  useEffect(() => {
    setStates(getStates(initialValues.eventCountry));
  }, [initialValues.eventCountry]);

  // Set date range (2 years before, 2 years after)
  useEffect(() => {
    const today = new Date();
    const twoYearsBefore = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate())
      .toISOString()
      .split('T')[0];

    // Reset date object to today before calculating future date
    const currentDate = new Date();
    const twoYearsAfter = new Date(
      currentDate.getFullYear() + 2,
      currentDate.getMonth(),
      currentDate.getDate()
    )
      .toISOString()
      .split('T')[0];

    setDateRange({ min: twoYearsBefore, max: twoYearsAfter });
  }, []);

  /**
   * Handle changes to the country select, updating states accordingly
   * @param {Object} e - Event object
   */
  const handleCountryChange = e => {
    const { name, value } = e.target;

    // Update form values
    setFormValues(prev => ({
      ...prev,
      [name]: value,
    }));

    // Update states list based on selected country
    setStates(getStates(value));
  };

  /**
   * Check if date is within the valid range
   * @param {string} dateStr - Date string to validate
   * @returns {boolean} - True if date is valid
   */
  const isDateInRange = dateStr => {
    const inputDate = new Date(dateStr);
    const minDate = new Date(dateRange.min);
    const maxDate = new Date(dateRange.max);
    return inputDate >= minDate && inputDate <= maxDate;
  };

  /**
   * Handle date changes with validation
   * @param {Object} event - Event object
   */
  const handleDateChange = event => {
    const { value } = event.target;
    if (!value || isDateInRange(value)) {
      setWeddingDate(value);
    }
  };

  /**
   * Handle generic form field changes
   * @param {Object} e - Event object
   */
  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="testi-header-container" role="form" aria-labelledby="user-info-heading">
      <div className="testi-header-content">
        <h2 className="testi-header-subtitle" id="user-info-heading">
          Your Information
        </h2>

        {/* Row 1: Order ID, Event Date, Country */}
        <div className="testi-header-row">
          <div className="testi-header-field">
            <label className="testi-header-label" htmlFor="orderId">
              <span>
                <b>Order ID:</b>
              </span>
            </label>
            <input
              type="hidden"
              id="externalOrderID"
              name="externalOrderID"
              className="testi-header-input"
              disabled
              value={externalOrderID}
              aria-label="External Order ID"
            />
            <input
              type="text"
              id="orderId"
              name="orderId"
              className="testi-header-input"
              disabled
              value={orderID}
              aria-label="Order ID"
            />
          </div>

          <FormField
            id="eventDate"
            label="Event Date"
            type="date"
            value={weddingDate}
            onChange={handleDateChange}
            ariaLabel="Event Date (Required)"
            required={true}
            min={dateRange.min}
            max={dateRange.max}
          />

          <FormField
            id="country"
            label="Country"
            type="select"
            value={formValues.country}
            onChange={handleCountryChange}
            ariaLabel="Event Country"
            defaultValue={initialValues.eventCountry}>
            <option value="">Select your Country</option>
            <option value="USA">USA</option>
            <option value="Canada">Canada</option>
          </FormField>
        </div>

        {/* Row 2: City, State, Empty */}
        <div className="testi-header-row">
          <FormField
            id="city"
            label="City"
            value={formValues.city}
            onChange={handleInputChange}
            ariaLabel="Event City"
            defaultValue={initialValues.eventCity}
          />

          <FormField
            id="state"
            label="State"
            type="select"
            value={formValues.state}
            onChange={handleInputChange}
            ariaLabel="Event State"
            defaultValue={initialValues.eventState}>
            {states.map(state => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </FormField>

          {/* Empty third column to maintain the grid layout */}
          <div className="testi-header-field testi-header-empty"></div>
        </div>
      </div>
    </div>
  );
};

TestiHeader.propTypes = {
  orderID: PropTypes.string.isRequired,
  externalOrderID: PropTypes.string.isRequired,
  data: PropTypes.oneOfType([
    PropTypes.shape({
      shipping_city: PropTypes.string,
      shipping_country: PropTypes.string,
      shipping_state: PropTypes.string,
      event_date: PropTypes.string,
    }),
    PropTypes.shape({
      data: PropTypes.shape({
        shipping_city: PropTypes.string,
        shipping_country: PropTypes.string,
        shipping_state: PropTypes.string,
        event_date: PropTypes.string,
      }),
    }),
  ]).isRequired,
};

export { TestiHeader };
